import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SignalPulse } from "./components/SignalPulse";
import { CryptoProvider } from "./context/CryptoContext";
import { RealtimeProvider } from "./context/RealtimeContext";
import { ExternalLinkProvider } from "./context/ExternalLinkContext";
import { TipsProvider } from "./components/CoachTip";
import { AppShell } from "./components/AppShell";
import { OrientationProvider } from "./components/OrientationTour";
import { StorageBanner } from "./components/StorageBanner";
import { ConnectionBanner } from "./components/ConnectionBanner";
import { Toaster } from "sonner";
import { PushPrompt } from "./components/PushPrompt";
import { syncExistingPush } from "./lib/push";
import { captureAttribution } from "./lib/attribution";
import CryptoGate from "./components/CryptoGate";
import { MfaSetup } from "./components/MfaSetup";
import Gate, { afterLogin } from "./pages/Gate";
import Pending from "./pages/Pending";
import Feed from "./pages/Feed";
import Directory from "./pages/Directory";
import Profile from "./pages/Profile";
import Messages from "./pages/Messages";
import Admin from "./pages/Admin";
import ContactCouncil from "./pages/ContactCouncil";
import CouncilMessages from "./pages/CouncilMessages";
import CreatePost from "./pages/CreatePost";
import Saved from "./pages/Saved";
import SinglePost from "./pages/SinglePost";
import Donate from "./pages/Donate";
import BeaconEarnings from "./pages/BeaconEarnings";
import EncryptedMeetUp from "./pages/EncryptedMeetUp";
import Stream from "./pages/Stream";
import SovereignVoices from "./pages/SovereignVoices";
import { SovereignCreed, CREED_VERSION } from "./components/SovereignCreed";
import Charter from "./components/Charter";
import PublicCompass from "./pages/PublicCompass";
import PublicAbout from "./pages/PublicAbout";
import MfaNudge from "./components/MfaNudge";
import Welcome from "./pages/Welcome";
import Collection from "./pages/Collection";
import Beacon from "./pages/Beacon";
import Hearth from "./pages/Hearth";
import Hearths from "./pages/Hearths";
import BeaconStorefront from "./pages/BeaconStorefront";
import BeaconTeaser from "./pages/BeaconTeaser";

const Splash = () => {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 9000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center gap-8 p-6 text-center">
      <SignalPulse size={84} />
      {slow && (
        <div data-testid="splash-slow-fallback">
          <p className="font-body text-sm text-zinc-500 mb-4">Taking longer than usual to connect.</p>
          <button
            onClick={() => window.location.reload()}
            data-testid="splash-reload-button"
            className="border border-white/15 text-zinc-300 px-5 py-2.5 font-body text-sm hover:bg-white/5 transition-colors"
          >
            Reload
          </button>
        </div>
      )}
    </div>
  );
};

const MfaGate = () => (
  <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
    <div className="w-full max-w-md">
      <p className="font-body text-[10px] tracking-[0.4em] uppercase text-[#14A89A] mb-4">Required for Council &amp; admins</p>
      <div className="border border-white/10 bg-[#121212] p-6">
        <MfaSetup forced onDone={() => window.location.reload()} />
      </div>
    </div>
  </div>
);

const Member = () => {
  const { user, ready } = useAuth();
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeText, setComposeText] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    const h = (e) => { setComposeText(e.detail?.text || ""); setComposeOpen(true); };
    window.addEventListener("sovsig:compose", h);
    return () => window.removeEventListener("sovsig:compose", h);
  }, []);

  useEffect(() => { if (user && (user.role === "admin" || user.status === "approved")) syncExistingPush(); }, [user]);

  if (!ready) return <Splash />;
  if (!user) {
    if (window.location.pathname === "/hq") sessionStorage.setItem("ss_after_login", "/hq");
    return <Navigate to="/" replace />;
  }
  if (user.role !== "admin" && user.status !== "approved") return <Navigate to="/pending" replace />;
  // Mandatory member agreement: hard-block ALL routes (and the crypto gate) until
  // the ACTIVE Creed version (server-driven, so the Council can re-prompt everyone)
  // is accepted. Rendered standalone so nothing mounts underneath.
  const activeCreed = user.active_creed_version || CREED_VERSION;
  if (user.creed_version !== activeCreed) return <SovereignCreed />;
  // Elevated accounts (Founder / admin / any Council perm) MUST have 2FA. Hard-block
  // every route until they enroll, mirrors the Creed gate.
  if (user.mfa_enforced && !user.mfa_enabled) return <MfaGate />;
  if (window.location.pathname === "/hq") sessionStorage.removeItem("ss_after_login");

  return (
    <OrientationProvider>
      <AppShell onCompose={() => { setComposeText(""); setComposeOpen(true); }}>
        <CryptoGate />
        <Routes>
          <Route path="/feed" element={<Feed refreshKey={refreshKey} />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/saved" element={<Saved />} />
          <Route path="/profile/:id" element={<Profile />} />
          <Route path="/hq" element={<Navigate to={`/profile/${user.id}`} replace />} />
          <Route path="/collection/:id" element={<Collection />} />
          <Route path="/beacon" element={<Beacon />} />
          <Route path="/beacon/earnings" element={<BeaconEarnings />} />
          <Route path="/hearths" element={<Hearths />} />
          <Route path="/hearth/:id" element={<Hearth />} />
          <Route path="/beacon/view/:handle" element={<BeaconStorefront />} />
          <Route path="/post/:id" element={<SinglePost />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/messages/:id" element={<Messages />} />
          <Route path="/council" element={<ContactCouncil />} />
          <Route path="/council/messages" element={<CouncilMessages />} />
          <Route path="/compass" element={<Charter />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/meet" element={<EncryptedMeetUp />} />
          <Route path="/stream" element={<Stream />} />
          <Route path="/voices" element={<SovereignVoices />} />
          <Route path="/admin" element={user.role === "admin" ? <Admin /> : <Navigate to="/feed" replace />} />
          <Route path="*" element={<Navigate to="/feed" replace />} />
        </Routes>
        <CreatePost open={composeOpen} initialText={composeText} onClose={() => { setComposeOpen(false); setComposeText(""); }} onCreated={() => setRefreshKey((k) => k + 1)} />
        <PushPrompt />
        <MfaNudge />
      </AppShell>
    </OrientationProvider>
  );
};

const Root = () => {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (user && (user.role === "admin" || user.status === "approved")) return <Navigate to={afterLogin()} replace />;
  if (user && user.status === "pending") return <Navigate to="/pending" replace />;
  return <Gate />;
};

const PendingRoute = () => {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role === "admin" || user.status === "approved") return <Navigate to="/feed" replace />;
  return <Pending />;
};

function App() {
  useEffect(() => { captureAttribution(); }, []);
  return (
    <div className="App">
      <StorageBanner />
      <ConnectionBanner />
      <Toaster theme="dark" position="top-center" richColors closeButton />
      <AuthProvider>
        <CryptoProvider>
          <RealtimeProvider>
            <ExternalLinkProvider>
              <TipsProvider>
                <BrowserRouter>
                  <Routes>
                    <Route path="/" element={<Root />} />
                    <Route path="/c" element={<PublicCompass />} />
                    <Route path="/about" element={<PublicAbout />} />
                    <Route path="/security" element={<Navigate to="/c#security" replace />} />
                    <Route path="/b/:handle" element={<BeaconTeaser />} />
                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/pending" element={<PendingRoute />} />
                    <Route path="/*" element={<Member />} />
                  </Routes>
                </BrowserRouter>
              </TipsProvider>
            </ExternalLinkProvider>
          </RealtimeProvider>
        </CryptoProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
